/**
 * FO-459 (batch-62) - pure briefpapier-resolver.
 *
 * Twee stappen, gedeeld door de API (elk generatiepad + de factuur-renderer) en
 * de backoffice-kiezer (de live resultaatregel), zodat kiezer en generatie
 * precies hetzelfde uitrekenen:
 *
 *  1. `bepaalBriefpapierVoorstel` - de VOORGEVULDE keuze (cascade):
 *       expliciete keuze > standaard van het template > standaard van de soort >
 *       laatste keuze van de gebruiker (voorstel) > BV van de context > algemeen.
 *  2. `bepaalBriefpapierToepassing` - wat er met die keuze feitelijk wordt
 *     toegepast, met ZICHTBARE terugval (nooit stil kaal papier):
 *       exact (BV x variant) > zelfde BV andere variant > algemeen zelfde variant >
 *       algemeen andere variant > het ingebouwde STREEP-briefpapier.
 *     Plus de waarschuwingen: ontbrekend briefpapier, "zonder handtekeningvak"
 *     op een te ondertekenen document, en expliciete keuze over een sjabloon met
 *     eigen briefpapier (dubbele kop/voet, FO-410 blijft verder de uitzondering).
 *
 * Facturen hebben een eigen, even pure tak (`bepaalFactuurBriefpapier`) met de
 * harde Finqle-regel: via Finqle ALTIJD de IBAN-loze variant (FO-011).
 */
import {
  BRIEFPAPIER_DOCUMENTSOORT_DEFS,
  INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING,
  briefpapierOmschrijving,
  type BriefpapierBvModus,
  type BriefpapierDocumentsoort,
  type BriefpapierKeuzeBron,
  type BriefpapierKeuzeInvoer,
  type BriefpapierToepassingDTO,
  type BriefpapierVariant,
  type BriefpapierWaarschuwing,
  type FactuurBriefpapierVariant,
  type FactuurLayout,
} from '@streep/types'

// ─── 1. Voorstel (cascade) ────────────────────────────────────────────────────

/** Een standaard of onthouden keuze: hoe de BV gekozen wordt + de variant. */
export interface BriefpapierVoorkeurRegel {
  bvModus: BriefpapierBvModus
  /** Alleen relevant bij VASTE_BV. */
  entityId: string | null
  variant: BriefpapierVariant
}

export interface BriefpapierVoorstelInvoer {
  documentsoort: BriefpapierDocumentsoort
  /** Expliciete keuze uit de kiezer (wint altijd). */
  expliciet: BriefpapierKeuzeInvoer | null
  /** Beheer-standaard van het ClientDocumentTemplate. */
  templateStandaard: BriefpapierVoorkeurRegel | null
  /** Beheer-standaard van de vaste generatiesoort. */
  soortStandaard: BriefpapierVoorkeurRegel | null
  /** Laatste keuze van deze gebruiker voor deze soort (alleen zonder beheer-standaard). */
  gebruikerVoorkeur: BriefpapierVoorkeurRegel | null
  /** BV van de context (medewerker-BV, klant-BV, template-BV). */
  contextEntityId: string | null
}

export interface BriefpapierVoorstel {
  keuze: BriefpapierKeuzeInvoer
  keuzeBron: BriefpapierKeuzeBron
}

function bvVoorRegel(regel: BriefpapierVoorkeurRegel, contextEntityId: string | null): string | null {
  switch (regel.bvModus) {
    case 'VASTE_BV':
      // Een vaste BV zonder BV kan niet (CHECK + schema); defensief: de context.
      return regel.entityId ?? contextEntityId
    case 'ALGEMEEN':
      return null
    case 'VOLG_CONTEXT':
      return contextEntityId
  }
}

export function bepaalBriefpapierVoorstel(inv: BriefpapierVoorstelInvoer): BriefpapierVoorstel {
  if (inv.expliciet) {
    return {
      keuze: { entityId: inv.expliciet.entityId, variant: inv.expliciet.variant },
      keuzeBron: 'EXPLICIET',
    }
  }
  const regels: Array<[BriefpapierVoorkeurRegel | null, BriefpapierKeuzeBron]> = [
    [inv.templateStandaard, 'TEMPLATE_STANDAARD'],
    [inv.soortStandaard, 'SOORT_STANDAARD'],
    [inv.gebruikerVoorkeur, 'GEBRUIKER_VOORKEUR'],
  ]
  for (const [regel, bron] of regels) {
    if (regel) {
      return {
        keuze: { entityId: bvVoorRegel(regel, inv.contextEntityId), variant: regel.variant },
        keuzeBron: bron,
      }
    }
  }
  const variant = BRIEFPAPIER_DOCUMENTSOORT_DEFS[inv.documentsoort].standaardVariant
  if (inv.contextEntityId) {
    return { keuze: { entityId: inv.contextEntityId, variant }, keuzeBron: 'CONTEXT' }
  }
  return { keuze: { entityId: null, variant }, keuzeBron: 'ALGEMEEN' }
}

/**
 * Vertaal een expliciete keuze naar de te onthouden voorkeur. Onthouden wordt
 * alleen de VARIANT en of bewust het algemene briefpapier gekozen is; een andere
 * BV dan die van de context wordt NIET als vaste BV onthouden. Anders kreeg elk
 * volgend document van deze soort (ook voor een medewerker of relatie van een
 * andere BV, en ook een hergeneratie zonder kiezer) dat BV-briefpapier
 * voorgesteld. Een vaste BV is een beheer-instelling (template-/soort-standaard).
 */
export function voorkeurUitKeuze(
  keuze: BriefpapierKeuzeInvoer,
  contextEntityId: string | null,
): BriefpapierVoorkeurRegel {
  if (keuze.entityId === null && contextEntityId !== null) {
    return { bvModus: 'ALGEMEEN', entityId: null, variant: keuze.variant }
  }
  return { bvModus: 'VOLG_CONTEXT', entityId: null, variant: keuze.variant }
}

// ─── 2. Toepassing (terugval + waarschuwingen) ────────────────────────────────

/** Een actief bibliotheek-briefpapier met bestand (Word-briefpapier). */
export interface BriefpapierBibliotheekItem {
  sjabloonId: string
  versieId: string | null
  versieNr: number | null
  entityId: string | null
  variant: BriefpapierVariant
  /** FO-477 - het voorblad van dit briefpapier is een losse (lege) pagina. */
  voorbladLos: boolean
}

export interface BriefpapierToepassingInvoer {
  gevraagd: BriefpapierKeuzeInvoer
  keuzeBron: BriefpapierKeuzeBron
  beschikbaar: readonly BriefpapierBibliotheekItem[]
  /** Naam per BV-id (voor de omschrijvingen). */
  entiteitNamen: Readonly<Record<string, string>>
  /** Wordt het document ondertekend? */
  ondertekening: boolean
  /**
   * FO-410: `true` = het sjabloon is gemarkeerd met eigen briefpapier;
   * `'detecteer'` = pad zonder beheerde vlag (DOCX-detectie beslist); `false` = nee.
   */
  eigenBriefpapier: boolean | 'detecteer'
}

export interface BriefpapierToepassing extends BriefpapierToepassingDTO {
  /**
   * De FO-410-keuze voor de conversie. Een expliciete keuze wint van het
   * sjabloon-eigen briefpapier (false); zonder expliciete keuze blijft de vlag
   * of de detectie gelden.
   */
  eigenBriefpapierRoute: boolean | 'detecteer'
}

const ANDERE_VARIANT: Record<BriefpapierVariant, BriefpapierVariant> = {
  MET_HANDTEKENING: 'ZONDER_HANDTEKENING',
  ZONDER_HANDTEKENING: 'MET_HANDTEKENING',
}

export const WAARSCHUWING_ZONDER_HANDTEKENINGVAK =
  'Dit document wordt ondertekend, maar het briefpapier heeft geen handtekeningvak. Kies de variant met handtekeningvak als de tekenflow ruimte nodig heeft.'

export const WAARSCHUWING_DUBBEL_BRIEFPAPIER =
  'Dit sjabloon heeft eigen briefpapier. Je gekozen briefpapier wordt eroverheen gelegd: controleer het resultaat op een dubbele kop of voet.'

/** Zoek een exacte (BV x variant)-combinatie. */
export function zoekBriefpapier(
  beschikbaar: readonly BriefpapierBibliotheekItem[],
  entityId: string | null,
  variant: BriefpapierVariant,
): BriefpapierBibliotheekItem | null {
  return beschikbaar.find((b) => b.entityId === entityId && b.variant === variant) ?? null
}

function entiteitNaam(
  entityId: string | null,
  namen: Readonly<Record<string, string>>,
): string | null {
  if (!entityId) return null
  return namen[entityId] ?? 'Onbekende BV'
}

export function bepaalBriefpapierToepassing(inv: BriefpapierToepassingInvoer): BriefpapierToepassing {
  const { gevraagd } = inv
  const waarschuwingen: BriefpapierWaarschuwing[] = []
  const expliciet = inv.keuzeBron === 'EXPLICIET'

  if (expliciet && inv.eigenBriefpapier === true) {
    waarschuwingen.push({ code: 'BRIEFPAPIER_DUBBEL_RISICO', melding: WAARSCHUWING_DUBBEL_BRIEFPAPIER })
  }

  const kandidaten: Array<[string | null, BriefpapierVariant]> =
    gevraagd.entityId !== null
      ? [
          [gevraagd.entityId, gevraagd.variant],
          [gevraagd.entityId, ANDERE_VARIANT[gevraagd.variant]],
          [null, gevraagd.variant],
          [null, ANDERE_VARIANT[gevraagd.variant]],
        ]
      : [
          [null, gevraagd.variant],
          [null, ANDERE_VARIANT[gevraagd.variant]],
        ]

  const gevraagdOmschrijving = briefpapierOmschrijving({
    entityNaam: entiteitNaam(gevraagd.entityId, inv.entiteitNamen),
    variant: gevraagd.variant,
  })

  let gevonden: BriefpapierBibliotheekItem | null = null
  for (const [entityId, variant] of kandidaten) {
    gevonden = zoekBriefpapier(inv.beschikbaar, entityId, variant)
    if (gevonden) break
  }

  const basis = {
    gevraagd: { entityId: gevraagd.entityId, variant: gevraagd.variant },
    keuzeBron: inv.keuzeBron,
    eigenBriefpapierRoute: expliciet ? false : inv.eigenBriefpapier,
    // FO-477 - het ingebouwde briefpapier is altijd MET_INHOUD; een bibliotheek-
    // briefpapier overschrijft dit met de modus van zijn sjabloon. Sjabloon-eigen
    // briefpapier krijgt zijn (afgeleide) modus pas bij de render (FO-485,
    // `sjabloonEigenToepassing` in de generatie-conversie).
    voorbladLos: false,
  }

  if (gevonden) {
    const terugval = gevonden.entityId !== gevraagd.entityId || gevonden.variant !== gevraagd.variant
    const toegepastNaam = entiteitNaam(gevonden.entityId, inv.entiteitNamen)
    if (terugval) {
      waarschuwingen.push({
        code: 'BRIEFPAPIER_ONTBREEKT',
        melding: `"${gevraagdOmschrijving}" staat niet actief in de bibliotheek; "${briefpapierOmschrijving({
          entityNaam: toegepastNaam,
          variant: gevonden.variant,
          voorbladLos: gevonden.voorbladLos,
        })}" is gebruikt.`,
      })
    }
    if (inv.ondertekening && gevonden.variant === 'ZONDER_HANDTEKENING') {
      waarschuwingen.push({
        code: 'BRIEFPAPIER_ZONDER_HANDTEKENINGVAK',
        melding: WAARSCHUWING_ZONDER_HANDTEKENINGVAK,
      })
    }
    return {
      ...basis,
      toepassing: 'BIBLIOTHEEK',
      sjabloonId: gevonden.sjabloonId,
      versieId: gevonden.versieId,
      versieNr: gevonden.versieNr,
      entityId: gevonden.entityId,
      variant: gevonden.variant,
      voorbladLos: gevonden.voorbladLos,
      omschrijving: briefpapierOmschrijving({
        entityNaam: toegepastNaam,
        variant: gevonden.variant,
        versieNr: gevonden.versieNr,
        voorbladLos: gevonden.voorbladLos,
      }),
      // FO-485 - de kale naam voor de resultaatregel ("... v3 - voorblad los -
      // route: bibliotheek"); de modus komt van het bibliotheek-sjabloon.
      briefpapierNaam: briefpapierOmschrijving({ entityNaam: toegepastNaam, variant: gevonden.variant }),
      voorbladBron: 'BIBLIOTHEEK',
      terugval,
      waarschuwingen,
    }
  }

  // Niets in de bibliotheek: het ingebouwde STREEP-briefpapier. Voor een
  // algemene keuze is dat gewoon het algemene briefpapier (geen terugval); voor
  // een BV-keuze is het een zichtbare terugval.
  const terugval = gevraagd.entityId !== null
  if (terugval) {
    waarschuwingen.push({
      code: 'BRIEFPAPIER_ONTBREEKT',
      melding: `"${gevraagdOmschrijving}" staat niet actief in de bibliotheek en er is geen algemeen alternatief; het ${INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING.charAt(0).toLowerCase()}${INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING.slice(1)} is gebruikt.`,
    })
  }
  return {
    ...basis,
    toepassing: 'INGEBOUWD',
    sjabloonId: null,
    versieId: null,
    versieNr: null,
    entityId: null,
    variant: null,
    omschrijving: INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING,
    // FO-485 - het ingebouwde STREEP-briefpapier heeft geen los voorblad (FO-477).
    briefpapierNaam: INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING,
    voorbladBron: 'GEEN_VOORBLAD',
    terugval,
    waarschuwingen,
  }
}

// ─── Facturen ─────────────────────────────────────────────────────────────────

/** Een actief factuur-briefpapier (of een vastgelegde versie). */
export interface FactuurBriefpapierItem {
  sjabloonId: string
  versieId: string | null
  versieNr: number | null
  entityId: string | null
  variant: FactuurBriefpapierVariant
}

export type FactuurBriefpapierBron =
  | { soort: 'BIBLIOTHEEK'; item: FactuurBriefpapierItem }
  | { soort: 'LEGACY_PAD'; pad: string }
  | { soort: 'INGEBOUWD' }
  | { soort: 'ONTBREEKT' }

export interface FactuurBriefpapierInvoer {
  /** De facturerende BV. */
  entityId: string
  /** Loopt de factuur via Finqle (toonFactoring, incl. FO-129 forceFinqle)? */
  viaFinqle: boolean
  /** Bij definitief maken vastgelegde versie (bevroren, FO-036); null = nog niet. */
  vastgelegd: FactuurBriefpapierItem | null
  /** Per-factuur-uitzondering (actieve versie van het gekozen sjabloon). */
  uitzondering: FactuurBriefpapierItem | null
  /** Er is een uitzondering ingesteld maar het sjabloon is niet (meer) bruikbaar. */
  uitzonderingOnbruikbaar: boolean
  /** Actieve factuur-briefpapieren met bestand (BV + algemeen). */
  beschikbaar: readonly FactuurBriefpapierItem[]
  /** Oude velden (tot de migratie is uitgevoerd): Entity.invoiceTemplatePath / -Finqle. */
  legacyStandaardPad: string | null
  legacyFinqlePad: string | null
  entiteitNamen: Readonly<Record<string, string>>
}

export interface FactuurBriefpapierResolutie {
  bron: FactuurBriefpapierBron
  variant: FactuurBriefpapierVariant
  keuzeBron: BriefpapierKeuzeBron
  omschrijving: string
  terugval: boolean
  waarschuwingen: string[]
}

export const WAARSCHUWING_FINQLE_UITZONDERING =
  'De uitzondering op deze factuur geldt niet: via Finqle krijgt de factuur altijd het briefpapier zonder IBAN.'

function factuurItem(
  beschikbaar: readonly FactuurBriefpapierItem[],
  entityId: string | null,
  variant: FactuurBriefpapierVariant,
): FactuurBriefpapierItem | null {
  return beschikbaar.find((b) => b.entityId === entityId && b.variant === variant) ?? null
}

function factuurOmschrijving(
  item: FactuurBriefpapierItem,
  namen: Readonly<Record<string, string>>,
): string {
  return briefpapierOmschrijving({
    entityNaam: entiteitNaam(item.entityId, namen),
    variant: item.variant,
    versieNr: item.versieNr,
  })
}

/**
 * De layout van een factuur-briefpapier zonder (geldige) render-instellingen:
 * algemeen papier draagt geen BV-kop, dus AFZENDERBLOK; per-BV papier heeft de
 * BV-kop en -voet ingebakken.
 */
export function standaardFactuurLayout(entityId: string | null): FactuurLayout {
  return entityId === null ? 'AFZENDERBLOK' : 'INGEBAKKEN_KOP'
}

export function bepaalFactuurBriefpapier(inv: FactuurBriefpapierInvoer): FactuurBriefpapierResolutie {
  const bvNaam = inv.entiteitNamen[inv.entityId] ?? 'de BV'

  if (inv.viaFinqle) {
    // FO-011 - HARD: via Finqle altijd de IBAN-loze variant; niet overrulebaar.
    const waarschuwingen = inv.uitzondering || inv.uitzonderingOnbruikbaar
      ? [WAARSCHUWING_FINQLE_UITZONDERING]
      : []
    const basis = { variant: 'ZONDER_IBAN' as const, terugval: false, waarschuwingen }
    if (inv.vastgelegd && inv.vastgelegd.variant === 'ZONDER_IBAN') {
      return {
        ...basis,
        bron: { soort: 'BIBLIOTHEEK', item: inv.vastgelegd },
        keuzeBron: 'VASTGELEGD',
        omschrijving: factuurOmschrijving(inv.vastgelegd, inv.entiteitNamen),
      }
    }
    const bv = factuurItem(inv.beschikbaar, inv.entityId, 'ZONDER_IBAN')
    if (bv) {
      return {
        ...basis,
        bron: { soort: 'BIBLIOTHEEK', item: bv },
        keuzeBron: 'FINQLE_VAST',
        omschrijving: factuurOmschrijving(bv, inv.entiteitNamen),
      }
    }
    if (inv.legacyFinqlePad) {
      return {
        ...basis,
        bron: { soort: 'LEGACY_PAD', pad: inv.legacyFinqlePad },
        keuzeBron: 'FINQLE_VAST',
        omschrijving: `${bvNaam} - factuur zonder IBAN`,
      }
    }
    const algemeen = factuurItem(inv.beschikbaar, null, 'ZONDER_IBAN')
    if (algemeen) {
      return {
        ...basis,
        bron: { soort: 'BIBLIOTHEEK', item: algemeen },
        keuzeBron: 'FINQLE_VAST',
        omschrijving: factuurOmschrijving(algemeen, inv.entiteitNamen),
      }
    }
    return {
      ...basis,
      bron: { soort: 'INGEBOUWD' },
      keuzeBron: 'FINQLE_VAST',
      omschrijving: INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING,
    }
  }

  const waarschuwingen: string[] = []
  if (inv.vastgelegd && inv.vastgelegd.variant === 'STANDAARD') {
    return {
      bron: { soort: 'BIBLIOTHEEK', item: inv.vastgelegd },
      variant: 'STANDAARD',
      keuzeBron: 'VASTGELEGD',
      omschrijving: factuurOmschrijving(inv.vastgelegd, inv.entiteitNamen),
      terugval: false,
      waarschuwingen,
    }
  }
  if (inv.uitzondering && inv.uitzondering.variant === 'STANDAARD') {
    return {
      bron: { soort: 'BIBLIOTHEEK', item: inv.uitzondering },
      variant: 'STANDAARD',
      keuzeBron: 'FACTUUR_UITZONDERING',
      omschrijving: factuurOmschrijving(inv.uitzondering, inv.entiteitNamen),
      terugval: false,
      waarschuwingen,
    }
  }
  if (inv.uitzonderingOnbruikbaar) {
    waarschuwingen.push(
      'Het uitzonderings-briefpapier van deze factuur is niet meer actief; het standaard factuur-briefpapier is gebruikt.',
    )
  }
  const bv = factuurItem(inv.beschikbaar, inv.entityId, 'STANDAARD')
  if (bv) {
    return {
      bron: { soort: 'BIBLIOTHEEK', item: bv },
      variant: 'STANDAARD',
      keuzeBron: 'FACTUUR_BV_STANDAARD',
      omschrijving: factuurOmschrijving(bv, inv.entiteitNamen),
      terugval: inv.uitzonderingOnbruikbaar,
      waarschuwingen,
    }
  }
  if (inv.legacyStandaardPad) {
    return {
      bron: { soort: 'LEGACY_PAD', pad: inv.legacyStandaardPad },
      variant: 'STANDAARD',
      keuzeBron: 'FACTUUR_BV_STANDAARD',
      omschrijving: `${bvNaam} - factuur standaard`,
      terugval: inv.uitzonderingOnbruikbaar,
      waarschuwingen,
    }
  }
  const algemeen = factuurItem(inv.beschikbaar, null, 'STANDAARD')
  if (algemeen) {
    waarschuwingen.push(
      `${bvNaam} heeft geen eigen factuur-briefpapier; het algemene factuur-briefpapier is gebruikt.`,
    )
    return {
      bron: { soort: 'BIBLIOTHEEK', item: algemeen },
      variant: 'STANDAARD',
      keuzeBron: 'FACTUUR_BV_STANDAARD',
      omschrijving: factuurOmschrijving(algemeen, inv.entiteitNamen),
      terugval: true,
      waarschuwingen,
    }
  }
  return {
    bron: { soort: 'ONTBREEKT' },
    variant: 'STANDAARD',
    keuzeBron: 'FACTUUR_BV_STANDAARD',
    omschrijving: `${bvNaam} heeft geen factuur-briefpapier`,
    terugval: false,
    waarschuwingen,
  }
}
